import { useContext, useEffect, useRef, useState } from "react";
import { BaseFieldSet } from "../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../components/form/label/styled";
import { BaseSelect } from "../../../components/form/select/styled";
import { AppLayout } from "../../../containers/layout/app";
import { IncomeStatementReportWrapper } from "./styled";
import { BaseOption } from "../../../components/form/option/styled";
import {
	Box,
	Checkbox,
	CircularProgress,
	Grid2,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	SelectChangeEvent,
	Stack,
	Typography,
} from "@mui/material";
import { BaseInput } from "../../../components/form/input/styled";
import { useQuery } from "@tanstack/react-query";
import { retrieveAllBranchesService } from "../../../utils/api/configuration/branch/retrieveAllBranches";
import Cookies from "universal-cookie";
import { BaseButton } from "../../../components/button/styled";
import { IncomeStatementReportTable } from "../../../containers/tables/incomestatementreporttable";
import { retrieveIncomeStatementReportService } from "../../../utils/api/report/incomestatement/retrieveIncomeStatementReport";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { AppContext } from "../../../context/appContext";
import { BaseDropDownType } from "../../../types/component.type";
import { downloadIncomeStatementReportService } from "../../../utils/api/report/incomestatement/downloadIncomeStatementReport";

export const IncomeStatementReport = () => {
	const downloadMenuItems = [
		{
			title: "PDF",
			url: "pdf",
		},
		{
			title: "Excel",
			url: "excel",
		},
	];

	const cookies = new Cookies();
	const TOKEN = cookies.getAll().TOKEN;

	const { setActiveNavItem } = useContext(AppContext);
	const dropdownRef = useRef<HTMLUListElement | null>(null);

	const [error, setError] = useState<string | null>(null);
	const [isDownloading, setIsDownloading] = useState(false);
	const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false);
	const [isFiltering, setIsFiltering] = useState(false);
	const [formDetails, setFormDetails] = useState({
		branchId: [],
		startDate: "",
		endDate: "",
	});
	const [incomeStatement, setIncomeStatement] = useState<
		Record<string, any>[] | null
	>(null);
	const [paginationIndex, setPaginationIndex] = useState({
		page: "1",
		perPage: "10",
		totalPages: "1",
	});

	useEffect(() => {
		setActiveNavItem("Report");
	}, [setActiveNavItem]);

	const { data: branches } = useQuery({
		queryKey: ["branchesForIncomeStatementReport", TOKEN],
		queryFn: async () => {
			const response = await retrieveAllBranchesService(TOKEN);
			return response?.data;
		},
		enabled: !!TOKEN,
	});

	const handleChange = (
		e:
			| React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
			| SelectChangeEvent<unknown>,
	) => {
		const { name, value } = e.target;
		if (name === "branchId" && String(value).includes("*")) {
			if (formDetails?.branchId?.length === branches?.data?.length) {
				return setFormDetails((prev) => ({
					...prev,
					[name]: [],
				}));
			}
			return setFormDetails((prev) => ({
				...prev,
				[name]: branches?.data?.map((b: Record<string, any>) => b.id) ?? [],
			}));
		}
		setFormDetails((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleOpenDownloadMenuClick = () => {
		if (isDownloading) return;
		return setIsDownloadMenuOpen(true);
	};

	const handleDropDownClickOutside = (event: MouseEvent) => {
		if (
			dropdownRef.current &&
			!dropdownRef.current.contains(event.target as Node)
		) {
			return setIsDownloadMenuOpen(false);
		}
	};

	const handleDownloadIncomeStatementReport = async (
		e: React.MouseEvent<HTMLLIElement, MouseEvent>,
		type: string,
	) => {
		e.preventDefault();
		setIsDownloading(true);
		try {
			const blob = await downloadIncomeStatementReportService(
				TOKEN,
				formDetails,
				type,
			);
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download =
				type === "pdf"
					? "income_statement_report.pdf"
					: "income_statement_report.xlsx";
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error: any) {
			console.error("Income statement report download failed:", error);
		} finally {
			setIsDownloading(false);
		}
	};

	const handleDownloadMenuItemClick = async (
		e: React.MouseEvent<HTMLLIElement, MouseEvent>,
		item: BaseDropDownType,
	) => {
		e.preventDefault();
		switch (item.title) {
			case "PDF":
			case "Excel":
				await handleDownloadIncomeStatementReport(e, item.url);
				break;
			default:
				break;
		}
		return setIsDownloadMenuOpen(false);
	};

	const handleFilterSubmit = async (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
	) => {
		e.preventDefault();
		setError(null);
		setIsFiltering(true);
		setIncomeStatement(null);
		try {
			const response = await retrieveIncomeStatementReportService(
				TOKEN,
                formDetails,
                paginationIndex
			);
			if (!Array.isArray(response) && response?.meta) {
				setPaginationIndex((prev) => ({
					...prev,
					totalPages: String(response?.meta?.totalPages ?? "1"),
				}));
			}
			if (response?.data.length > 0) {
				setIncomeStatement(response?.data ?? []);
			} else {
				setError("No income or expenditure GLs found for selected filters.");
			}
		} catch (error: any) {
			setError("No income or expenditure GLs found for selected filters.");
			console.error("Income statement report generation failed:", error);
		} finally {
			setIsFiltering(false);
		}
	};

	const handlePagination = (
		e: React.MouseEvent<HTMLButtonElement>,
		type: "previous" | "next",
	) => {
		e.preventDefault();
		setPaginationIndex((prev) => {
			const currentPage = parseInt(prev.page, 10);
			const newPage =
				type === "previous"
					? Math.max(1, currentPage - 1)
					: Math.min(parseInt(prev.totalPages, 10), currentPage + 1);
			return {
				...prev,
				page: newPage.toString(),
			};
		});
	};

	useEffect(() => {
		if (isDownloadMenuOpen) {
			document.addEventListener("mousedown", handleDropDownClickOutside);
		} else {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleDropDownClickOutside);
		};
	}, [isDownloadMenuOpen]);

	return (
		<AppLayout
			pageId={"Income-Statement-Report"}
			pageTitle={"Income Statement Report"}
		>
			<IncomeStatementReportWrapper>
				<form className="filter-bar">
					<BaseFieldSet>
						<BaseFieldSet>
							<BaseLabel colour="var(--dark-color)">Branch</BaseLabel>
							<BaseSelect
								required
								multiple
								displayEmpty
								name="branchId"
								fontweight={400}
								inputProps={{
									renderValue: (selected: string[]) => {
										if (!selected || selected.length === 0) {
											return <em>Select Branch</em>;
										}
										if (selected.length === branches?.data?.length) {
											return "All Branches";
										}
										const selectedNames = branches
											?.data
											?.filter((b: Record<string, any>) =>
												selected.includes(b.id),
											)
											.map((b: Record<string, any>) => b.name);
										return selectedNames?.join(", ");
									},
								}}
								value={(formDetails.branchId ?? []).map((f: any) => f) ?? []}
								colour="var(--semi-gray-color)"
								onChange={(e) => handleChange(e)}
							>
								<BaseOption value="*" sx={{ gap: "calc(var(--flex-gap)/2)" }}>
									<Box>
										<Checkbox
											checked={
												(formDetails.branchId ?? [])?.length ===
												branches?.data?.length
											}
											sx={{
												padding: 0,
												"& .MuiSvgIcon-root": { fill: "inherit" },
											}}
										/>
									</Box>
									<Box>
										<Typography
											component={"span"}
											fontFamily={"inherit"}
											fontWeight={"inherit"}
											fontSize={"inherit"}
											lineHeight={"normal"}
											color={"inherit"}
											whiteSpace={"normal"}
										>
											Select All Branches
										</Typography>
									</Box>
								</BaseOption>
								{branches?.data?.map((branch: Record<string, any>, index: number) => {
									return (
										<BaseOption
											key={index}
											value={branch.id}
											sx={{
												alignItems: "center",
												gap: "calc(var(--flex-gap)/2)",
												"&.Mui-selected": {
													backgroundColor: "transparent",
												},
											}}
										>
											<Box>
												<Checkbox
													checked={(formDetails.branchId ?? [])?.some(
														(b: string) => b === branch.id,
													)}
													sx={{
														padding: 0,
														"& .MuiSvgIcon-root": { fill: "inherit" },
													}}
												/>
											</Box>
											<Box>
												<Typography
													component={"span"}
													fontFamily={"inherit"}
													fontWeight={"inherit"}
													fontSize={"inherit"}
													lineHeight={"normal"}
													color={"inherit"}
													whiteSpace={"normal"}
												>
													{branch.name}
												</Typography>
											</Box>
										</BaseOption>
									);
								})}
							</BaseSelect>
						</BaseFieldSet>
					</BaseFieldSet>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">Start Date</BaseLabel>
						<BaseInput
							type="date"
							name="startDate"
							fontweight={400}
							value={formDetails.startDate}
							colour="var(--semi-gray-color)"
							sx={{ width: "-webkit-fill-available" }}
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
					<BaseFieldSet>
						<BaseLabel colour="var(--dark-color)">End Date</BaseLabel>
						<BaseInput
							type="date"
							name="endDate"
							fontweight={400}
							value={formDetails.endDate}
							colour="var(--semi-gray-color)"
							sx={{ width: "-webkit-fill-available" }}
							onChange={(e) => handleChange(e)}
						/>
					</BaseFieldSet>
					<Box component={"div"} className="filter-button-box">
						<BaseButton
							variant="contained"
							padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
							sx={{
								width: { mobile: "-webkit-fill-available", miniTablet: "auto" },
							}}
							onClick={handleFilterSubmit}
						>
							{isFiltering ? (
								<CircularProgress color="inherit" className="loader" />
							) : (
								<Typography
									variant={"button"}
									fontFamily={"inherit"}
									fontWeight={"inherit"}
									fontSize={"inherit"}
									lineHeight={"inherit"}
									color={"inherit"}
									textTransform={"inherit"}
								>
									Filter
								</Typography>
							)}
						</BaseButton>
					</Box>
				</form>
				<Box>
					{error && (
						<Typography
							fontFamily={"Inter"}
							fontWeight={"600"}
							fontSize={14}
							lineHeight={"normal"}
							color={"#FF0000"}
							whiteSpace={"normal"}
						>
							{error}
						</Typography>
					)}
				</Box>
				{incomeStatement && (
					<Box component={"div"} className="income-statement-report-table-box">
						<IncomeStatementReportTable
							rows={incomeStatement as Record<string, any>[]}
						/>
					</Box>
				)}
				<Grid2 container component={"div"} spacing={"var(--flex-gap)"}>
					<Grid2
						size={{ mobile: 12, laptop: 8 }}
						display={{ mobile: "none", laptop: "grid" }}
					></Grid2>
					<Grid2 size={{ mobile: 12, laptop: 4 }}>
						<Stack
							direction={"row"}
							overflow={"hidden"}
							borderRadius={"6px"}
							alignItems={"center"}
							justifyContent={"space-around"}
							border={"1px solid var(--input-field-border-color)"}
						>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									disabled={
										parseInt(paginationIndex.page, 10) === 1 || isFiltering
									}
									onClick={(e) => handlePagination(e, "previous")}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Previous
									</Typography>
								</BaseButton>
							</Box>
							<Box
								display={"flex"}
								overflow={"hidden"}
								bgcolor={"var(--primary-color)"}
								padding={"calc(var(--basic-padding)/2)"}
							>
								<Typography
									variant="h3"
									fontFamily={"Roboto"}
									fontWeight={600}
									fontSize={10}
									lineHeight={"normal"}
									color="var(--light-color)"
								>
									{`${paginationIndex.page} / ${paginationIndex.totalPages}`}
								</Typography>
							</Box>
							<Box overflow={"hidden"} display={"flex"}>
								<BaseButton
									radius="0"
									border="none"
									variant="text"
									disableElevation
									colour={"var(--dark-color)"}
									padding="0 calc(var(--basic-padding)/2)"
									onClick={(e) => handlePagination(e, "next")}
									disabled={
										paginationIndex.page === paginationIndex.totalPages ||
										isFiltering
									}
								>
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Next
									</Typography>
								</BaseButton>
							</Box>
						</Stack>
					</Grid2>
				</Grid2>
				{incomeStatement && (
					<Stack className="call-to-action-stack">
						<Box>
							<BaseButton
								variant="contained"
								endIcon={<KeyboardArrowDownIcon />}
								padding="calc(var(--basic-padding)/4) calc(var(--basic-padding))"
								sx={{
									width: {
										mobile: "-webkit-fill-available",
										miniTablet: "auto",
									},
								}}
								onClick={handleOpenDownloadMenuClick}
							>
								{isDownloading ? (
									<CircularProgress color="inherit" className="loader" />
								) : (
									<Typography
										variant={"button"}
										fontFamily={"inherit"}
										fontWeight={"inherit"}
										fontSize={"inherit"}
										lineHeight={"inherit"}
										color={"inherit"}
										textTransform={"inherit"}
									>
										Download
									</Typography>
								)}
							</BaseButton>
						</Box>
						{isDownloadMenuOpen && (
							<List
								component={"ul"}
								ref={dropdownRef}
								className="dropdown-modal"
							>
								{downloadMenuItems.map((item, index) => (
									<ListItem
										key={index}
										onClick={(e) => handleDownloadMenuItemClick(e, item)}
									>
										<ListItemButton>
											<ListItemText primary={item.title} />
										</ListItemButton>
									</ListItem>
								))}
							</List>
						)}
					</Stack>
				)}
			</IncomeStatementReportWrapper>
		</AppLayout>
	);
};
